import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { appToast } from '../../../components/AppToast'
import { useBirthdaysEntryScreen } from './hooks/useBirthdaysEntryScreen'

export default function BirthdaysEntryScreen() {
  const { state, actions } = useBirthdaysEntryScreen()

  useEffect(() => {
    if (state.error) appToast.error(state.error)
  }, [state.error])

  useEffect(() => {
    if (state.success) appToast.success(state.success)
  }, [state.success])

  if (state.isLoading) {
    return (
      <div className="mx-auto flex w-full max-w-[1460px] items-center justify-center rounded-2xl border border-outline-variant/25 bg-surface-container-lowest px-6 py-20 text-sm text-on-surface-variant">
        <span className="material-symbols-outlined animate-spin text-2xl text-primary mr-2">
          progress_activity
        </span>
        Loading birthday details...
      </div>
    )
  }

  if (!state.student) {
    return (
      <div className="mx-auto flex w-full max-w-[1460px] flex-col items-center justify-center rounded-2xl border border-outline-variant/25 bg-surface-container-lowest px-6 py-20 text-sm text-on-surface-variant">
        <span className="material-symbols-outlined text-4xl text-on-surface-variant/40 mb-2">
          person_off
        </span>
        <p className="font-semibold text-on-surface">Student not found.</p>
        <Link
          to="/admission/birthdays/list"
          className="mt-3 inline-flex items-center gap-1 rounded-xl bg-primary/10 px-4 py-1.5 text-xs font-bold text-primary hover:bg-primary/20 transition cursor-pointer"
        >
          Back to List
        </Link>
      </div>
    )
  }

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-12">
      {/* ── Page Header ─────────────────────────────────────────────────────── */}
      <section className="flex flex-col gap-4 border-b border-outline-variant/10 pb-5 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.16em] text-primary">
            <span>Admission Module</span>
            <span className="material-symbols-outlined text-xs">chevron_right</span>
            <span>Birthdays</span>
          </div>
          <h1 className="mt-2 font-display text-3xl font-extrabold tracking-tight text-on-surface">
            Send Birthday Wish
          </h1>
          <p className="mt-1 text-sm text-on-surface-variant">
            Send a personalized birthday greeting to {state.student.firstName} {state.student.lastName}.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Link
            to="/admission/birthdays/list"
            className="inline-flex items-center gap-1.5 rounded-xl border border-outline-variant/30 bg-surface-container-low px-4 py-2.5 text-xs font-bold text-on-surface-variant transition-colors hover:bg-surface-container-high cursor-pointer"
          >
            <span className="material-symbols-outlined text-base">arrow_back</span>
            Back to List
          </Link>
        </div>
      </section>

      {state.error && (
        <div className="rounded-xl border border-error/30 bg-error-container px-4 py-3 text-sm text-error">
          {state.error}
        </div>
      )}

      {/* ── Student Profile & Birthday Info ─────────────────────────────────── */}
      <section className="space-y-6">
        <article className="rounded-2xl border border-outline-variant/25 bg-surface-container-lowest p-6 shadow-sm sm:p-8">
          <div className="mb-6 flex items-center gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
              <span className="material-symbols-outlined text-3xl">cake</span>
            </div>
            <div>
              <h2 className="text-2xl font-extrabold text-on-surface">
                {state.student.firstName} {state.student.lastName}
              </h2>
              <p className="text-xs font-semibold text-on-surface-variant">
                {state.student.studentCode || `Roll No: ${state.student.rollNo || '—'}`}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-xl border border-outline-variant/20 bg-surface-container-low/50 p-4">
              <div className="text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">Birth Date</div>
              <p className="mt-1 text-lg font-extrabold text-on-surface">
                {new Date(state.student.dob).toLocaleDateString('en-IN', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                })}
              </p>
              <p className="text-xs text-on-surface-variant/80">{state.student.birthMonth}</p>
            </div>

            <div className="rounded-xl border border-outline-variant/20 bg-surface-container-low/50 p-4">
              <div className="text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">Current Age</div>
              <p className="mt-1 text-lg font-extrabold text-on-surface">{state.student.age || '—'} years</p>
              <p className="text-xs text-on-surface-variant/80">Born on {state.student.birthDate} {state.student.birthMonth}</p>
            </div>

            <div className="rounded-xl border border-outline-variant/20 bg-surface-container-low/50 p-4">
              <div className="text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">Email</div>
              <p className="mt-1 text-sm font-semibold text-on-surface truncate">{state.student.email || '—'}</p>
              <p className="text-xs text-on-surface-variant/80">Contact Email</p>
            </div>

            <div className="rounded-xl border border-outline-variant/20 bg-surface-container-low/50 p-4">
              <div className="text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">Mobile</div>
              <p className="mt-1 text-sm font-semibold text-on-surface tabular-nums">{state.student.mobile || '—'}</p>
              <p className="text-xs text-on-surface-variant/80">Phone Number</p>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="rounded-xl border border-outline-variant/20 bg-surface-container-low/50 p-4">
              <div className="text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">Course</div>
              <p className="mt-1 text-sm font-semibold text-on-surface">{state.student.course || '—'}</p>
            </div>

            <div className="rounded-xl border border-outline-variant/20 bg-surface-container-low/50 p-4">
              <div className="text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">Batch</div>
              <p className="mt-1 text-sm font-semibold text-on-surface">{state.student.batch || '—'}</p>
            </div>

            <div className="rounded-xl border border-outline-variant/20 bg-surface-container-low/50 p-4">
              <div className="text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">Branch</div>
              <p className="mt-1 text-sm font-semibold text-on-surface">{state.student.branch || '—'}</p>
            </div>
          </div>
        </article>

        {/* ── Birthday Message Card ───────────────────────────────────────────── */}
        <article className="rounded-2xl border border-outline-variant/25 bg-surface-container-lowest p-6 shadow-sm sm:p-8">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="h-6 w-1.5 rounded-full bg-primary" />
              <h2 className="text-xl font-bold text-on-surface">Birthday Message</h2>
            </div>

            {/* Channel Selection */}
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-on-surface-variant">Send Via:</span>
              <div className="inline-flex rounded-xl bg-surface-container-high p-1">
                {[
                  { key: 'both', label: 'SMS & Email', icon: 'all_inbox' },
                  { key: 'email', label: 'Email', icon: 'mail' },
                  { key: 'sms', label: 'SMS', icon: 'sms' },
                ].map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => actions.setChannel(item.key)}
                    className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold transition-colors cursor-pointer ${
                      state.channel === item.key
                        ? 'bg-surface-container-lowest text-primary shadow-xs'
                        : 'text-on-surface-variant hover:text-on-surface'
                    }`}
                  >
                    <span className="material-symbols-outlined text-sm">{item.icon}</span>
                    {item.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Quick Greeting Templates */}
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <span className="text-[11px] font-bold uppercase tracking-wider text-on-surface-variant">
              Quick Templates:
            </span>
            <button
              type="button"
              onClick={() =>
                actions.setWishMessage(
                  `Wishing you a very Happy Birthday, ${state.student.firstName}! May this year bring you great joy, academic excellence, and abundant success.`
                )
              }
              className="rounded-lg border border-outline-variant/25 bg-surface-container-low px-2.5 py-1 text-xs font-semibold text-on-surface-variant hover:border-primary/40 hover:text-primary transition cursor-pointer"
            >
              Academic Success
            </button>
            <button
              type="button"
              onClick={() =>
                actions.setWishMessage(
                  `Warmest birthday greetings, ${state.student.firstName}! May your year ahead be filled with happiness, good health, and wonderful achievements.`
                )
              }
              className="rounded-lg border border-outline-variant/25 bg-surface-container-low px-2.5 py-1 text-xs font-semibold text-on-surface-variant hover:border-primary/40 hover:text-primary transition cursor-pointer"
            >
              Warm Wishes
            </button>
            <button
              type="button"
              onClick={() =>
                actions.setWishMessage(
                  `Happy Birthday ${state.student.firstName}! On behalf of everyone at ${state.student.company || 'our institution'}, we wish you a joyous celebration!`
                )
              }
              className="rounded-lg border border-outline-variant/25 bg-surface-container-low px-2.5 py-1 text-xs font-semibold text-on-surface-variant hover:border-primary/40 hover:text-primary transition cursor-pointer"
            >
              Institution Greeting
            </button>
          </div>

          <label className="grid gap-2">
            <span className="text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">
              Enter Your Wish
            </span>
            <textarea
              value={state.wishMessage}
              onChange={(event) => actions.setWishMessage(event.target.value)}
              placeholder="Write a heartfelt birthday message..."
              rows="5"
              className="w-full rounded-xl border border-outline-variant/30 bg-surface-container-low/40 px-4 py-3.5 text-sm text-on-surface outline-none placeholder:text-on-surface-variant/60 transition-all focus:border-primary focus:ring-2 focus:ring-primary/20"
            />
          </label>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={actions.handleSendWish}
              disabled={state.isSending || !state.wishMessage.trim()}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-6 py-2.5 text-xs font-bold text-on-primary shadow-sm hover:bg-primary/90 transition cursor-pointer disabled:cursor-not-allowed disabled:opacity-60"
            >
              <span className="material-symbols-outlined text-base">send</span>
              {state.isSending ? 'Sending...' : 'Send Birthday Wish'}
            </button>
            <Link
              to="/admission/birthdays/list"
              className="inline-flex items-center justify-center rounded-xl border border-outline-variant/30 bg-surface-container-low px-6 py-2.5 text-xs font-bold text-on-surface transition hover:bg-surface-container-high cursor-pointer"
            >
              Back to List
            </Link>
          </div>
        </article>
      </section>
    </div>
  )
}
